/**
 * @fileoverview Разбор смены: что давило на Безопасность и куда ушли очки.
 */

import { MODES } from "../constants/modes";
import { frontStage } from "../state/createGame";
import type { GameState } from "../state/types";

export interface DebriefLine {
  /** Короткий заголовок фактора */
  title: string;
  /** Пояснение */
  detail: string;
  /** bad | warn | good | info */
  tone: "bad" | "warn" | "good" | "info";
}

export interface ShiftDebrief {
  /** Давление на индекс безопасности */
  pressure: DebriefLine[];
  /** Куда ушли / откуда пришли очки */
  scoreNotes: DebriefLine[];
  /** Лучший выбор */
  best: string;
  /** Худший урок */
  lesson: string;
  /** Практичный совет на следующую смену */
  tip: string;
  /** Однострочное резюме */
  summary: string;
}

function pushPressure(
  out: DebriefLine[],
  title: string,
  detail: string,
  tone: DebriefLine["tone"],
  weight: number,
): void {
  if (weight <= 0) return;
  out.push({ title, detail, tone });
}

/**
 * Собрать разбор по финальному состоянию и счетчикам смены.
 */
export function buildDebrief(g: GameState): ShiftDebrief {
  const pressure: DebriefLine[] = [];
  const snow0 = Math.round(g.runways[0]?.snow || 0);
  const snow1 = Math.round(g.runways[1]?.snow || 0);
  const ice0 = Math.round(g.runways[0]?.ice || 0);
  const ice1 = Math.round(g.runways[1]?.ice || 0);
  const maxSnow = Math.max(snow0, snow1);
  const maxIce = Math.max(ice0, ice1);
  const approachN = g.planes.filter(
    (p) => !p.dead && p.status === "approach",
  ).length;

  pushPressure(
    pressure,
    "Снег на полосах",
    `К концу: 09L ${snow0}% · 27R ${snow1}%`,
    maxSnow > 70 ? "bad" : maxSnow > 40 ? "warn" : "info",
    maxSnow,
  );
  pushPressure(
    pressure,
    "Гололед",
    `К концу: 09L ${ice0}% · 27R ${ice1}%`,
    maxIce > 40 ? "bad" : maxIce > 15 ? "warn" : "info",
    maxIce,
  );
  pushPressure(
    pressure,
    "Нарушения",
    `${g.violations} крит. (−12 к Безопасности каждое)`,
    g.violations > 0 ? "bad" : "info",
    g.violations,
  );
  pushPressure(
    pressure,
    "Рисковые операции",
    `${g.riskyOps} подтвержденных рисковых посадок/взлетов/решений (−3 каждое)`,
    g.riskyOps >= 4 ? "bad" : g.riskyOps > 0 ? "warn" : "info",
    g.riskyOps,
  );
  pushPressure(
    pressure,
    "Очередь в воздухе",
    `${approachN} борт(а) еще на орбите`,
    approachN >= 3 ? "bad" : "warn",
    approachN,
  );
  if (g.runways[0]?.closed && g.runways[1]?.closed) {
    pressure.push({
      title: "Обе полосы закрыты",
      detail: "Сильный удар по Безопасности (−16)",
      tone: "bad",
    });
  }
  if (g.paxMood < 55) {
    pressure.push({
      title: "Настроение пассажиров",
      detail: `${Math.round(g.paxMood)} / 100 — копились задержки и очередь`,
      tone: g.paxMood < 35 ? "bad" : "warn",
    });
  }

  // Сортируем: bad → warn → остальное, максимум 5
  const rank = { bad: 0, warn: 1, info: 2, good: 3 };
  pressure.sort((a, b) => rank[a.tone] - rank[b.tone]);
  const pressureTop = pressure.slice(0, 5);

  const scoreNotes: DebriefLine[] = [];
  const log = g.scoreLog || [];
  const gains = [...log].filter((e) => e.v > 0).sort((a, b) => b.v - a.v);
  const losses = [...log].filter((e) => e.v < 0).sort((a, b) => a.v - b.v);

  for (const e of gains.slice(0, 3)) {
    scoreNotes.push({
      title: `+${e.v} · ${e.label}`,
      detail: "Сильный плюс смены",
      tone: "good",
    });
  }
  for (const e of losses.slice(0, 3)) {
    scoreNotes.push({
      title: `${e.v} · ${e.label}`,
      detail: "Потеря очков",
      tone: "bad",
    });
  }

  if (g.goarounds > 0) {
    scoreNotes.push({
      title: `Уходы на второй круг: ${g.goarounds}`,
      detail: "Каждый уход бьет по очкам и темпу",
      tone: "warn",
    });
  }
  if (g.delays > 0) {
    scoreNotes.push({
      title: `Задержки: ${g.delays}`,
      detail: "Простой без гейта / у полосы / срывы вылета",
      tone: "warn",
    });
  }
  if (g.taxiHits > 0) {
    scoreNotes.push({
      title: `Инциденты на рулежке: ${g.taxiHits}`,
      detail: "Сближения и столкновения на земле",
      tone: "bad",
    });
  }
  if (g.bestStreak >= 6) {
    scoreNotes.push({
      title: `Серия ×${g.bestStreak}`,
      detail: "Хороший ритм без срывов",
      tone: "good",
    });
  }

  const scoreTop = scoreNotes.slice(0, 6);

  const best =
    g.bestDecision ||
    (g.bestStreak >= 6
      ? `Держали серию до ${g.bestStreak}`
      : g.landings + g.departures > 8
        ? "Стабильный поток рейсов"
        : "Смена без ярких решений — в следующий раз ловите события вовремя");

  const lesson =
    g.worstMistake ||
    (g.minSafety < 40
      ? "Безопасность просела слишком низко — уборка и риск должны идти раньше очереди"
      : g.riskyOps >= 5
        ? "Слишком много рисковых кликов вместо ожидания уборки"
        : g.delays >= 4
          ? "Много простоев у гейта и у полосы"
          : "Критичных провалов не зафиксировано");

  let tip =
    "Держите хотя бы одну полосу чистой до пика фронта и не копите орбиту.";
  if (g.violations > 0) {
    tip = "Сначала снимите угрозу на полосе (уборка / реагент), потом темп.";
  } else if (maxSnow > 70 || g.riskyOps >= 4) {
    tip = "Грязная полоса: лучше подождать «Снег», чем подтверждать риск подряд.";
  } else if (g.delays >= 3) {
    tip = "Назначайте гейт сразу после посадки и не держите борт у hold-short.";
  } else if (g.minSafety >= 70 && g.score > 1200) {
    tip = "Сильная смена. Попробуйте сценарий «Метель» или более длинный режим.";
  } else if (g.scenarioId && g.scenarioId !== "free") {
    tip = "Сыграйте тот же сценарий еще раз — сравните мин. безопасность и задержки.";
  }

  const mode = MODES[g.modeId]?.label || g.modeId;
  const sc = g.scenarioLabel ? ` · сценарий «${g.scenarioLabel}»` : "";
  const summary = `${mode}${sc}. Фронт: «${frontStage(g).name}». Мин. безопасность ${g.minSafety}, настроение ${Math.round(g.paxMood)}.`;

  return {
    pressure: pressureTop,
    scoreNotes: scoreTop,
    best,
    lesson,
    tip,
    summary,
  };
}
